/**
 * Web `application_launch` / `application_first_launch` retention emit (Gap
 * G-8). Web has no foundation analytics package, so this module re-implements
 * the foundation `AnalyticsManager.recordLaunch` launch-lifecycle contract for
 * the browser and forwards through {@link reportAnalyticsEvent} (GA4 +
 * Amplitude). Without it, web retention cohorts (D1/D7/D30)
 * are invisible — iOS / Android emit these, web was the gap.
 *
 * FROZEN wire formats — byte-identical with the foundation contract
 * (`foundation-android/.../AnalyticsManager.kt` `recordLaunch`, mirrored on
 * iOS `MammothAnalytics`):
 *   - First ever launch:
 *       `application_first_launch`  (no params)
 *       `application_launch`        `{ days_from_today: "nil" }`  ← the literal
 *         string sentinel, NOT an omitted param. This is the SOLE documented
 *         exception to the catalog's OMIT-when-null rule (catalog §1.1) so the
 *         dashboard can distinguish "no prior launch" from "param dropped".
 *   - Every returning launch:
 *       `application_launch`        `{ days_from_today: <int> }`  ← whole
 *         calendar days between the previous launch and now (foundation uses
 *         `ChronoUnit.DAYS.between`; web mirrors with a UTC day-floor diff).
 * `application_first_launch` fires only on the first ever launch.
 *
 * Persistence: the previous-launch timestamp lives in `localStorage` under
 * {@link PREVIOUS_LAUNCH_DATE_KEY} (the web analogue of the native
 * `com.mmthstudio.previousLaunchDate` UserDefaults key). Absent → first
 * launch. The key is updated to "now" after each emit.
 *
 * Consent + safe-client: this module NEVER reads or writes anything itself
 * outside `recordWebLaunch`, and `recordWebLaunch` is wired as an analytics
 * `onAccept` callback in `consent.ts`, so it only runs once the user accepts
 * the analytics category. `reportAnalyticsEvent` is itself production-gated +
 * fail-safe, so a launch emit can never throw into app startup (safe-client
 * rule #2).
 */
import { reportAnalyticsEvent } from './webAnalyticsReporter'

/**
 * localStorage key holding the ISO-8601 timestamp of the previous launch.
 * Web analogue of the native `com.mmthstudio.previousLaunchDate` key — kept
 * under the same `com.mmthstudio.` namespace for cross-platform legibility,
 * though web storage is per-origin and never shared with the apps.
 */
export const PREVIOUS_LAUNCH_DATE_KEY = 'com.mmthstudio.previousLaunchDate'

/** First-launch sentinel — the frozen literal string, not an omitted param. */
const FIRST_LAUNCH_DAYS_SENTINEL = 'nil'

const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * Whole calendar days between two instants, floored to the UTC day boundary
 * — mirrors foundation's `ChronoUnit.DAYS.between` (which counts complete
 * 24h-aligned days). Same-day return → 0; never negative (a clock that went
 * backwards clamps to 0 rather than emitting a nonsensical negative count).
 */
export function daysBetween(previous: Date, now: Date): number {
  const prevDay = Math.floor(previous.getTime() / MS_PER_DAY)
  const nowDay = Math.floor(now.getTime() / MS_PER_DAY)
  return Math.max(0, nowDay - prevDay)
}

/**
 * Read the previous-launch timestamp from `localStorage`. Returns `null` on
 * first launch (key absent) or when storage is unavailable / the stored value
 * is unparseable — any of which is treated as "first launch" (fail-safe: a
 * launch event must never throw, and a fresh/cleared store is genuinely a
 * first launch from this origin's perspective).
 */
function readPreviousLaunchDate(): Date | null {
  try {
    const raw = window.localStorage.getItem(PREVIOUS_LAUNCH_DATE_KEY)
    if (raw === null) return null
    const ms = Date.parse(raw)
    if (Number.isNaN(ms)) return null
    return new Date(ms)
  } catch {
    // localStorage can throw (Safari private mode, disabled storage). Treat
    // as first launch — never propagate into startup.
    return null
  }
}

/** Persist `now` as the previous-launch timestamp. Best-effort; swallows. */
function writePreviousLaunchDate(now: Date): void {
  try {
    window.localStorage.setItem(PREVIOUS_LAUNCH_DATE_KEY, now.toISOString())
  } catch {
    // Storage write failed (private mode / quota) — the event already fired;
    // the only consequence is the next launch re-classifies as first. Swallow.
  }
}

/**
 * Emit the web launch-lifecycle events for this session and update the
 * persisted previous-launch timestamp. Idempotent-safe to call once per app
 * load (wired into the analytics `onAccept` consent callback). `now` is
 * injectable for tests.
 *
 * - First launch (no stored date): `application_first_launch` then
 *   `application_launch { days_from_today: "nil" }`.
 * - Returning launch: `application_launch { days_from_today: <int> }`.
 */
export function recordWebLaunch(now: Date = new Date()): void {
  const previous = readPreviousLaunchDate()
  if (previous === null) {
    reportAnalyticsEvent('application_first_launch', {})
    reportAnalyticsEvent('application_launch', {
      days_from_today: FIRST_LAUNCH_DAYS_SENTINEL,
    })
  } else {
    reportAnalyticsEvent('application_launch', {
      days_from_today: daysBetween(previous, now),
    })
  }
  writePreviousLaunchDate(now)
}
